"use strict";
var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
import { CsrfHeader } from "./accounts_payable/data/types/Pb";
import { reportUnretriedRejection } from "./csrf_report";
import { csrfToken, setCsrfToken } from "./csrf_token";
import {
  isCsrfRejection,
  retryInputFor,
  XhrRetry
} from "./csrf_retry";
import { FULLSTORY_URL_HEADER, fullStoryReplayUrl } from "./fullstory";
const ABSOLUTE_URL = /^(?:[a-z]+:)?\/\//i;
const LOG_MISSING_TOKEN = process.env.NODE_ENV !== "production";
const port = location.port ? `:${location.port}` : "";
const origin = `${location.protocol}//${location.hostname}${port}`;
function sameOriginUrl(target) {
  if (!ABSOLUTE_URL.test(target)) {
    return true;
  }
  try {
    return new URL(target, origin).origin === origin;
  } catch (e) {
    return false;
  }
}
function warnMissingToken(method, target) {
  console.warn(
    `csrf_xhr sent ${method} ${target} without a ${CsrfHeader.CsrfHeaderToken} header because the page has no <meta name="csrf-token"> content. Add csrf_meta_tags to the layout.`
  );
}
function refreshToken(token) {
  if (token) {
    setCsrfToken(token);
  }
}
function installXhr() {
  const CSRF_HEADER_LOWERCASE = CsrfHeader.CsrfHeaderToken.toLowerCase();
  const FULLSTORY_HEADER_LOWERCASE = FULLSTORY_URL_HEADER.toLowerCase();
  const tokenAlreadySet = /* @__PURE__ */ new WeakSet();
  const sameOriginOpen = /* @__PURE__ */ new WeakSet();
  const xhrRetries = /* @__PURE__ */ new WeakMap();
  const unwrappedRequests = /* @__PURE__ */ new WeakMap();
  const watchUnwrapped = (xhr) => {
    xhr.addEventListener("readystatechange", () => {
      const request = unwrappedRequests.get(xhr);
      if (request === void 0 || xhr.readyState !== XMLHttpRequest.HEADERS_RECEIVED || xhr.getResponseHeader(CsrfHeader.CsrfHeaderRejected) === null) {
        return;
      }
      reportUnretriedRejection(__spreadProps(__spreadValues({
        reason: "unwrapped_xhr",
        transport: "xhr"
      }, request), {
        status: xhr.status
      }));
    });
  };
  const watchForRefresh = (xhr) => {
    const xhrRetry = new XhrRetry(xhr);
    xhrRetries.set(xhr, xhrRetry);
    xhr.addEventListener("readystatechange", (event) => {
      if (
        // The caller already saw readyState 1 for the first attempt; letting
        // the retry's own open() fire it again would look like a second
        // request.
        xhrRetry.suppressIfRetrying(event) || xhr.readyState !== XMLHttpRequest.HEADERS_RECEIVED
      ) {
        return;
      }
      if (sameOriginOpen.has(xhr) && sameOriginUrl(xhr.responseURL)) {
        const refreshed = xhr.getResponseHeader(
          CsrfHeader.CsrfHeaderTokenRefresh
        );
        refreshToken(refreshed);
        const rejected = xhr.getResponseHeader(CsrfHeader.CsrfHeaderRejected);
        if (isCsrfRejection(xhr.status, rejected, refreshed)) {
          xhrRetry.suppressAndMarkForRetry(event);
        } else if (rejected !== null) {
          xhrRetry.reportMissingRefresh();
        }
      }
    });
  };
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;
  XMLHttpRequest.prototype.open = function(method, url, async = true, username, password) {
    const target = url.toString();
    const sameOrigin = sameOriginUrl(target);
    tokenAlreadySet.delete(this);
    const xhrRetry = xhrRetries.get(this);
    if (xhrRetry) {
      xhrRetry.recordOpen({ method, url, async, username, password });
    } else {
      if (!unwrappedRequests.has(this)) {
        watchUnwrapped(this);
      }
      unwrappedRequests.set(this, { method, url: target });
    }
    originalOpen.call(this, method, url, async, username, password);
    if (sameOrigin) {
      sameOriginOpen.add(this);
    } else {
      sameOriginOpen.delete(this);
    }
    const token = sameOrigin ? csrfToken() : null;
    if (token !== null) {
      this.setRequestHeader(CsrfHeader.CsrfHeaderToken, token);
    } else if (sameOrigin && LOG_MISSING_TOKEN) {
      warnMissingToken(method, target);
    }
    if (sameOrigin) {
      const replayUrl = fullStoryReplayUrl();
      if (replayUrl !== void 0) {
        this.setRequestHeader(FULLSTORY_URL_HEADER, replayUrl);
      }
    }
  };
  XMLHttpRequest.prototype.setRequestHeader = function(name, value) {
    var _a;
    const lowerName = name.toLowerCase();
    if (lowerName === CSRF_HEADER_LOWERCASE) {
      if (tokenAlreadySet.has(this)) {
        return;
      }
      tokenAlreadySet.add(this);
    } else if (lowerName !== FULLSTORY_HEADER_LOWERCASE) {
      (_a = xhrRetries.get(this)) == null ? void 0 : _a.recordHeader(lowerName, value);
    }
    originalSetRequestHeader.call(this, name, value);
  };
  const originalSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function(body) {
    var _a;
    (_a = xhrRetries.get(this)) == null ? void 0 : _a.recordBody(body != null ? body : null);
    originalSend.call(this, body);
  };
  const originalAbort = XMLHttpRequest.prototype.abort;
  XMLHttpRequest.prototype.abort = function() {
    var _a;
    (_a = xhrRetries.get(this)) == null ? void 0 : _a.recordAbort();
    originalAbort.call(this);
  };
  const NativeXMLHttpRequest = window.XMLHttpRequest;
  window.XMLHttpRequest = class extends NativeXMLHttpRequest {
    constructor() {
      super();
      watchForRefresh(this);
    }
  };
}
function installFetch() {
  const refreshedTokenFrom = (response) => sameOriginUrl(response.url) ? response.headers.get(CsrfHeader.CsrfHeaderTokenRefresh) : null;
  const withTokenRefresh = (sent, retryInput, init, headers, method, target) => sent.then((response) => {
    const refreshed = refreshedTokenFrom(response);
    refreshToken(refreshed);
    const rejected = response.headers.get(CsrfHeader.CsrfHeaderRejected);
    const report = (reason) => reportUnretriedRejection({
      reason,
      transport: "fetch",
      method,
      url: target,
      status: response.status
    });
    if (!isCsrfRejection(response.status, rejected, refreshed)) {
      if (rejected !== null) {
        report("no_refresh_header");
      }
      return response;
    }
    if (retryInput === null) {
      report(
        headers.get(CsrfHeader.CsrfHeaderRetry) === "1" ? "already_retried" : "body_not_replayable"
      );
      return response;
    }
    const retryHeaders = new Headers(headers);
    retryHeaders.set(CsrfHeader.CsrfHeaderToken, refreshed);
    retryHeaders.set(CsrfHeader.CsrfHeaderRetry, "1");
    return withTokenRefresh(
      originalFetch(retryInput, __spreadProps(__spreadValues({}, init), { headers: retryHeaders })),
      null,
      init,
      retryHeaders,
      method,
      target
    );
  });
  const originalFetch = globalThis.fetch;
  globalThis.fetch = function(input, init) {
    var _a, _b, _c;
    const request = typeof input !== "string" && !(input instanceof URL) ? input : null;
    const target = request ? request.url : input.toString();
    if (!sameOriginUrl(target)) {
      return originalFetch(input, init);
    }
    const token = csrfToken();
    const method = (_b = (_a = init == null ? void 0 : init.method) != null ? _a : request == null ? void 0 : request.method) != null ? _b : "GET";
    if (token === null && LOG_MISSING_TOKEN) {
      warnMissingToken(method, target);
    }
    const headers = new Headers((_c = init == null ? void 0 : init.headers) != null ? _c : request == null ? void 0 : request.headers);
    let addedHeader = false;
    if (token !== null && !headers.has(CsrfHeader.CsrfHeaderToken)) {
      headers.set(CsrfHeader.CsrfHeaderToken, token);
      addedHeader = true;
    }
    const replayUrl = fullStoryReplayUrl();
    if (replayUrl !== void 0 && !headers.has(FULLSTORY_URL_HEADER)) {
      headers.set(FULLSTORY_URL_HEADER, replayUrl);
      addedHeader = true;
    }
    const retryInput = retryInputFor(input, request, init, headers);
    const sent = addedHeader ? originalFetch(input, __spreadProps(__spreadValues({}, init), { headers })) : originalFetch(input, init);
    return withTokenRefresh(sent, retryInput, init, headers, method, target);
  };
}
const csrfWindow = window;
if (!csrfWindow.__csrfXhrInstalled) {
  installXhr();
  if (typeof globalThis.fetch === "function") {
    installFetch();
  }
  csrfWindow.__csrfXhrInstalled = true;
}
